import {httpGetIssueDate} from '@features/chatbot/services/getIssueDate.http.ts'
import {errorHandler} from '@utilities/helpers.ts'
import {useEffect, useState} from 'react'
import {useTranslation, Trans} from 'react-i18next'
import {useChatbotStore} from '@features/chatbot/store/chatbotStore.ts'
import {CHATBOT_SOURCES} from '@features/chatbot/types.ts'

interface GetIssueDateProps {
    flowSource: string
}

export const GetIssueDate = ({flowSource}: GetIssueDateProps) => {
    const {t} = useTranslation()
    const selectedShoot = useChatbotStore(store => store.selectedAssignment)
    const addStepToHistory = useChatbotStore(store => store.addStepToHistory)
    const chatbotRef = useChatbotStore(state => state.chatbotRef)
    const [isLoading, setIsLoading] = useState(true)
    const [issueDate, setIssueDate] = useState<string | null>(null)

    const {airbnbId, entityId, shootMediaId} = {
        airbnbId: selectedShoot?.entity?.external_id,
        entityId: selectedShoot?.entity?.external_id,
        shootMediaId: selectedShoot?.task_media?.id
    }

    useEffect(() => {
        onGetIssueDate()
    }, [])

    const onGetIssueDate = async () => {
        if (!entityId || !shootMediaId) {
            console.error('Missing required data for issue date lookup')
            setIsLoading(false)
            addStepToHistory({
                id: flowSource,
                source: CHATBOT_SOURCES.CHATBOT,
                text: `[Pull payment date error] The payment for Airbnb ID #${airbnbId} has not been issued yet. It will be issued within the next 14 working days. Thank you for your patience!`
            })
            return
        }

        try {
            const {data} = await httpGetIssueDate({
                entityId: Number(entityId),
                media: String(shootMediaId)
            })
            const paymentDate = data['payment_date']
            if (paymentDate) setIssueDate(paymentDate)

            addStepToHistory({
                id: flowSource,
                source: CHATBOT_SOURCES.CHATBOT,
                text: paymentDate
                    ? `The payment for Airbnb ID #${airbnbId} has been issued on ${paymentDate}. Please check the Transaction History in your Airbnb account for confirmation.`
                    : `The payment for Airbnb ID #${airbnbId} has not been issued yet. It will be issued within the next 14 working days. Thank you for your patience!`
            })
        } catch (e) {
            errorHandler(e)
            addStepToHistory({
                id: flowSource,
                source: CHATBOT_SOURCES.CHATBOT,
                text: `[Pull payment date error] The payment for Airbnb ID #${airbnbId} has not been issued yet. It will be issued within the next 14 working days. Thank you for your patience!`
            })
        } finally {
            setIsLoading(false)
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            await chatbotRef?.current?.triggerNextStep({trigger: 'send_log'})
        }
    }

    return isLoading ? (
        `${t('chatbot:get_issue_date:checking')}`
    ) : issueDate ? (
        <p>
            <Trans
                i18nKey="chatbot:get_issue_date:get_issue_date_yes"
                components={[<b />]}
                values={{airbnbId: airbnbId, issueDate: issueDate}}
            />
        </p>
    ) : (
        <p>
            <Trans
                i18nKey="chatbot:get_issue_date:get_issue_date_no"
                components={[<b />]}
                values={{airbnbId: airbnbId}}
            />
        </p>
    )
}
